import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
    Home, Map, MessageCircle, Heart, LayoutGrid, FileText, BookOpen, Compass,
    Stethoscope, BookHeart, Contact, SlidersHorizontal, MessageSquareQuote,
    LifeBuoy, ShieldCheck, X,
} from "lucide-react";
import { SECTION_COLORS } from "@/lib/sectionColors";

/* Mobile bottom navigation (Phase 2 production hardening).
 * The desktop pill nav wraps into a 200px+ sticky header on phones, so on
 * < md we hide it and surface the same "rooms" here instead: the four
 * highest-frequency rooms get permanent slots (Wellness is first-class per
 * the nav philosophy — never hidden), everything else lives one tap away in
 * the "More" bottom sheet. All routes stay reachable; nothing was deleted.
 */

const PRIMARY = [
    { to: "/dashboard", key: "nav.home", icon: Home, testid: "mobilenav-home", match: ["/dashboard"], colorKey: "home" },
    { to: "/roadmap", key: "nav.roadmap", icon: Map, testid: "mobilenav-roadmap", match: ["/roadmap", "/care-history"], colorKey: "roadmap" },
    { to: "/chat", key: "nav.askLane", icon: MessageCircle, testid: "mobilenav-lane", match: ["/chat"], colorKey: "askLane" },
    { to: "/wellness", key: "nav.wellness", icon: Heart, testid: "mobilenav-wellness", match: ["/wellness", "/burnout"], colorKey: "wellness" },
];

const SHEET_ROOMS = [
    { to: "/care-summary", key: "nav.careSummary", icon: FileText, testid: "mobilenav-care-summary", match: ["/care-summary", "/care-team-summary", "/appointment"], colorKey: "careSummary" },
    { to: "/education", key: "nav.learn", icon: BookOpen, testid: "mobilenav-learn", match: ["/education", "/treatments", "/guidance", "/conversations", "/who-pays"], colorKey: "learn" },
    { to: "/resources-hub", key: "nav.resources", icon: Compass, testid: "mobilenav-resources", match: ["/resources-hub", "/help-finder"], colorKey: "resources" },
    { to: "/guide", key: "nav.guide", icon: Stethoscope, testid: "mobilenav-guide", usOnly: true, match: ["/guide"], colorKey: "guide" },
];

const SHEET_MORE = [
    { to: "/journey", key: "nav.journey", icon: BookHeart, testid: "mobilenav-journey" },
    { to: "/care-team", key: "nav.careTeamMain", icon: Contact, testid: "mobilenav-care-team" },
    { to: "/settings", key: "nav.settings", icon: SlidersHorizontal, testid: "mobilenav-settings" },
    { to: "/feedback", key: "nav.feedback", icon: MessageSquareQuote, testid: "mobilenav-feedback" },
    { to: "/support", key: "nav.help", icon: LifeBuoy, testid: "mobilenav-help" },
    { to: "/privacy", key: "nav.privacy", icon: ShieldCheck, testid: "mobilenav-privacy" },
];

const isRouteActive = (pathname, item) =>
    (item.match || [item.to]).some(m => pathname === m || pathname.startsWith(m + "/"));

export const MobileBottomNav = ({ t, isUS }) => {
    const location = useLocation();
    const [sheetOpen, setSheetOpen] = useState(false);

    // Close the sheet whenever the route changes (a tap navigated somewhere).
    useEffect(() => { setSheetOpen(false); }, [location.pathname]);

    const sheetRooms = SHEET_ROOMS.filter(it => !it.usOnly || isUS);
    const moreActive = [...sheetRooms, ...SHEET_MORE].some(it => isRouteActive(location.pathname, it));

    return (
        <>
            <nav
                data-testid="mobile-bottom-nav"
                aria-label="Primary"
                className="md:hidden no-print fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-sm border-t border-[#E2E8F0] pb-[env(safe-area-inset-bottom)]"
            >
                <ul className="grid grid-cols-5">
                    {PRIMARY.map((it) => {
                        const active = isRouteActive(location.pathname, it);
                        const c = SECTION_COLORS[it.colorKey] || SECTION_COLORS.home;
                        return (
                            <li key={it.to}>
                                <NavLink
                                    to={it.to}
                                    data-testid={it.testid}
                                    aria-current={active ? "page" : undefined}
                                    className={`flex flex-col items-center justify-center gap-0.5 min-h-[56px] px-1 pt-1.5 pb-1 transition-colors ${active ? "" : "text-[#4C5551]"}`}
                                    style={active ? { color: "#3A6379" } : undefined}
                                >
                                    <span className="flex items-center justify-center w-11 h-7 rounded-full transition-colors"
                                        style={active ? { backgroundColor: "#EDF3F6" } : undefined}>
                                        <it.icon size={20} strokeWidth={active ? 2 : 1.6} style={{ color: active ? "#4F7F95" : "#6E7A74" }} />
                                    </span>
                                    <span className={`text-[10px] leading-tight truncate max-w-full ${active ? "font-semibold" : "font-medium"}`}>
                                        {t(it.key)}
                                    </span>
                                </NavLink>
                            </li>
                        );
                    })}
                    <li>
                        <button
                            type="button"
                            onClick={() => setSheetOpen(v => !v)}
                            data-testid="mobilenav-more"
                            aria-haspopup="dialog"
                            aria-expanded={sheetOpen}
                            className={`w-full flex flex-col items-center justify-center gap-0.5 min-h-[56px] px-1 pt-1.5 pb-1 transition-colors ${moreActive || sheetOpen ? "text-[#3A6379]" : "text-[#4C5551]"}`}
                        >
                            <span className={`flex items-center justify-center w-11 h-7 rounded-full transition-colors ${moreActive || sheetOpen ? "bg-teal-soft" : ""}`}>
                                <LayoutGrid size={20} strokeWidth={moreActive || sheetOpen ? 2 : 1.6} />
                            </span>
                            <span className={`text-[10px] leading-tight ${moreActive || sheetOpen ? "font-semibold" : "font-medium"}`}>
                                More
                            </span>
                        </button>
                    </li>
                </ul>
            </nav>

            {sheetOpen && (
                <div className="md:hidden fixed inset-0 z-50" role="dialog" aria-modal="true" data-testid="mobilenav-sheet">
                    <button
                        type="button"
                        aria-label="Close menu"
                        onClick={() => setSheetOpen(false)}
                        className="absolute inset-0 bg-black/40 backdrop-blur-[2px] animate-fade-in"
                        data-testid="mobilenav-sheet-backdrop"
                    />
                    <div className="absolute bottom-0 inset-x-0 bg-white rounded-t-3xl shadow-2xl p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] animate-fade-in-up max-h-[75vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-3">
                            <p className="font-heading font-semibold text-[#1F2624]">Everything else</p>
                            <button
                                type="button"
                                onClick={() => setSheetOpen(false)}
                                aria-label="Close"
                                data-testid="mobilenav-sheet-close"
                                className="w-11 h-11 -mr-2 flex items-center justify-center rounded-full text-[#4C5551] hover:bg-cream-dark"
                            >
                                <X size={18} strokeWidth={1.8} />
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mb-4">
                            {sheetRooms.map(it => {
                                const active = isRouteActive(location.pathname, it);
                                const c = SECTION_COLORS[it.colorKey] || SECTION_COLORS.home;
                                return (
                                    <NavLink
                                        key={it.to}
                                        to={it.to}
                                        data-testid={it.testid}
                                        className={`flex items-center gap-2.5 rounded-2xl border px-3.5 py-3 min-h-[52px] text-sm font-medium transition-colors ${active
                                            ? "text-white"
                                            : "text-[#1F2624] border-cream-dark"}`}
                                        style={active
                                            ? { backgroundColor: c.accent, borderColor: c.accent }
                                            : { backgroundColor: c.soft }}
                                    >
                                        <it.icon size={18} strokeWidth={1.7} className="shrink-0" style={{ color: active ? "#FFFFFF" : c.accent }} />
                                        <span className="leading-snug">{t(it.key)}</span>
                                    </NavLink>
                                );
                            })}
                        </div>
                        <div className="border-t border-cream-dark pt-3 grid grid-cols-2 gap-1">
                            {SHEET_MORE.map(it => (
                                <NavLink
                                    key={it.to}
                                    to={it.to}
                                    data-testid={it.testid}
                                    className={`flex items-center gap-2.5 rounded-xl px-3 py-3 min-h-[44px] text-sm font-medium transition-colors ${isRouteActive(location.pathname, it)
                                        ? "bg-teal text-white"
                                        : "text-[#4C5551] hover:bg-cream-light"}`}
                                >
                                    <it.icon size={16} strokeWidth={1.6} className="shrink-0" />
                                    {t(it.key)}
                                </NavLink>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default MobileBottomNav;
